import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Bootstrap, ActivityEvent } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, formatDayMonth } from "../lib/format";
import Select from "../components/Select";

const PAGE = 40;

// Amount-range filter presets (pence). amax is exclusive.
const AMOUNTS: { value: string; label: string; amin?: number; amax?: number }[] = [
  { value: "", label: "Any amount" },
  { value: "u10", label: "Under £10", amax: 1000 },
  { value: "10-20", label: "£10 – £20", amin: 1000, amax: 2000 },
  { value: "20-40", label: "£20 – £40", amin: 2000, amax: 4000 },
  { value: "40-60", label: "£40 – £60", amin: 4000, amax: 6000 },
  { value: "60-100", label: "£60 – £100", amin: 6000, amax: 10000 },
  { value: "100+", label: "£100 +", amin: 10000 },
];

const TYPES: { value: string; label: string }[] = [
  { value: "", label: "All types" },
  { value: "spend", label: "Spends" },
  { value: "moneyin", label: "Money in" },
  { value: "bill", label: "Recurring bills" },
  { value: "plan", label: "Payment plans" },
  { value: "savings", label: "Savings" },
  { value: "budget", label: "Budget" },
  { value: "balance", label: "Balance updates" },
  { value: "income", label: "Income" },
  { value: "setup", label: "Setup & people" },
];

const DATES = [
  { value: "", label: "Any time" },
  { value: "today", label: "Today" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "month", label: "This month" },
];

// Where a "View" link should go for each kind of thing.
const LINKS: Record<string, { to: string; label: string }> = {
  spend: { to: "/spending", label: "View in Spending" },
  balance: { to: "/spending", label: "View in Spending" },
  bill: { to: "/recurring", label: "View in Recurring" },
  plan: { to: "/plans", label: "View payment plans" },
  savings: { to: "/savings", label: "View savings" },
  budget: { to: "/budget", label: "View budget" },
  income: { to: "/settings", label: "View in Settings" },
  category: { to: "/settings", label: "View in Settings" },
  type: { to: "/settings", label: "View in Settings" },
  member: { to: "/settings", label: "View in Settings" },
  settings: { to: "/settings", label: "View in Settings" },
  paycycle: { to: "/", label: "View This Month" },
};

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

export default function Activity({ data }: { data: Bootstrap | null }) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // A from/to in the URL (e.g. a week link from the Safe-to-spend breakdown)
  // seeds a one-off custom date range.
  const [searchParams] = useSearchParams();
  const isDate = (x: string | null) => x != null && /^\d{4}-\d{2}-\d{2}$/.test(x);
  const [customFrom] = useState<string | null>(() => (isDate(searchParams.get("from")) ? searchParams.get("from") : null));
  const [customTo] = useState<string | null>(() => (isDate(searchParams.get("to")) ? searchParams.get("to") : null));

  // Filters
  const [member, setMember] = useState<number | null>(null);
  const [moneyOnly, setMoneyOnly] = useState(false);
  const [datePreset, setDatePreset] = useState(customFrom ? "custom" : "");
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");

  const members = data?.household.members ?? [];
  const categories = data?.categories ?? [];

  const { from, to } = useMemo(() => {
    if (datePreset === "custom") return { from: customFrom, to: customTo };
    if (datePreset === "today") return { from: isoDaysAgo(0), to: isoDaysAgo(0) };
    if (datePreset === "7") return { from: isoDaysAgo(6), to: isoDaysAgo(0) };
    if (datePreset === "30") return { from: isoDaysAgo(29), to: isoDaysAgo(0) };
    if (datePreset === "month") {
      const now = new Date();
      const first = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      return { from: first, to: isoDaysAgo(0) };
    }
    return { from: null as string | null, to: null as string | null };
  }, [datePreset, customFrom, customTo]);

  const dateOptions = customFrom
    ? [{ value: "custom", label: `${formatDayMonth(customFrom)}${customTo ? `–${formatDayMonth(customTo)}` : ""}` }, ...DATES]
    : DATES;

  const amt = AMOUNTS.find((a) => a.value === amount);
  const anyFilter = member != null || moneyOnly || datePreset || amount || type || category;

  const load = useCallback(
    async (offset: number) => {
      setLoading(true);
      try {
        const r = await api.activity({
          limit: PAGE, offset, member, money: moneyOnly,
          from, to,
          amin: amt?.amin ?? null, amax: amt?.amax ?? null,
          type: type || null,
          category: category ? Number(category) : null,
        });
        setEvents((prev) => (offset === 0 ? r.events : [...prev, ...r.events]));
        setHasMore(r.hasMore);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [member, moneyOnly, from, to, amt?.amin, amt?.amax, type, category],
  );

  useEffect(() => {
    load(0);
  }, [load]);

  function clearFilters() {
    setMember(null);
    setMoneyOnly(false);
    setDatePreset("");
    setAmount("");
    setType("");
    setCategory("");
  }

  const groups = groupByDay(events);

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Activity log</h1>
          <p className="page__lede">Every change in the tracker — who did what, and how the balance moved.</p>
        </div>
      </div>

      <div className="activity-filters">
        <div className="activity-chips">
          <button className={`activity-chip ${member == null ? "activity-chip--on" : ""}`} onClick={() => setMember(null)}>
            Everyone
          </button>
          {members.map((m) => (
            <button
              key={m.id}
              className={`activity-chip ${member === m.id ? "activity-chip--on" : ""}`}
              onClick={() => setMember(m.id)}
              style={member === m.id ? { borderColor: m.colour, color: m.colour } : undefined}
            >
              {m.name}
            </button>
          ))}
        </div>
        <label className="activity-money-toggle">
          <input type="checkbox" checked={moneyOnly} onChange={(e) => setMoneyOnly(e.target.checked)} />
          <span>Money movements only</span>
        </label>
      </div>

      <div className="activity-selects">
        <div className="activity-select"><Select ariaLabel="Filter by date" value={datePreset} onChange={setDatePreset} options={dateOptions} /></div>
        <div className="activity-select"><Select ariaLabel="Filter by amount" value={amount} onChange={setAmount} options={AMOUNTS.map((a) => ({ value: a.value, label: a.label }))} /></div>
        <div className="activity-select"><Select ariaLabel="Filter by type" value={type} onChange={setType} options={TYPES} /></div>
        <div className="activity-select">
          <Select
            ariaLabel="Filter by category"
            value={category}
            onChange={setCategory}
            options={[{ value: "", label: "All categories" }, ...categories.map((c) => ({ value: String(c.id), label: c.name }))]}
          />
        </div>
        {anyFilter && (
          <button className="btn btn--ghost btn--sm activity-clear" onClick={clearFilters}>Clear filters</button>
        )}
      </div>

      {error ? (
        <div className="state state--error" role="alert">{error}</div>
      ) : events.length === 0 && !loading ? (
        <div className="panel empty">
          {anyFilter ? "Nothing matches these filters." : "No activity yet. Changes you make will show up here."}
        </div>
      ) : (
        <div className="activity">
          {groups.map((g) => (
            <section className="activity-group" key={g.key}>
              <h2 className="activity-group__head">{g.label}</h2>
              <ul className="activity-list">
                {g.events.map((ev) => (
                  <ActivityRow key={ev.id} ev={ev} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {hasMore && !error && (
        <div className="activity-more">
          <button className="btn btn--ghost" disabled={loading} onClick={() => load(events.length)}>
            {loading ? "Loading…" : "Show older"}
          </button>
        </div>
      )}
    </div>
  );
}

function ActivityRow({ ev }: { ev: ActivityEvent }) {
  const [open, setOpen] = useState(false);
  const move = movement(ev);
  const initial = ev.actorInitial ?? (ev.actorName ? ev.actorName[0] : "?");
  const colour = ev.actorColour ?? "var(--color-fg-muted)";
  const expandable = ev.isMoney || ev.detail != null || ev.categoryName != null;
  const link = LINKS[ev.entity];
  const showPayee = ev.paidByName != null && (ev.action.startsWith("spend.") || ev.action.startsWith("moneyin."));

  return (
    <li className={`activity-item ${expandable ? "activity-item--expandable" : ""}`}>
      <button
        className="activity-item__row"
        onClick={() => expandable && setOpen((o) => !o)}
        aria-expanded={expandable ? open : undefined}
      >
        <span className="activity-item__who" style={{ background: colour }} title={ev.actorName ?? undefined}>
          {initial}
        </span>
        <span className="activity-item__body">
          <span className="activity-item__line">{describe(ev)}</span>
          <span className="activity-item__meta">
            {timeAgo(ev.at)}
            {showPayee && <> · paid by <strong>{ev.paidByName}</strong></>}
            {ev.categoryName && <> · {ev.categoryName}</>}
          </span>
        </span>
        {move.minor != null && (
          <span className={`activity-item__amt activity-item__amt--${move.dir}`}>
            {move.dir === "up" ? "+" : move.dir === "down" ? "−" : ""}
            {formatMoney(Math.abs(move.minor))}
          </span>
        )}
        {expandable && (
          <svg className={`activity-item__chev ${open ? "is-open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        )}
      </button>

      {open && (
        <div className="activity-item__detail">
          {ev.isMoney && ev.beforeMinor != null && ev.afterMinor != null && (
            <div className="activity-calc">
              <span>{balanceLabel(ev)}</span>
              <strong>{formatMoney(ev.beforeMinor)}</strong>
              <span className="activity-calc__arrow">→</span>
              <strong>{formatMoney(ev.afterMinor)}</strong>
            </div>
          )}
          {ev.isMoney && (ev.beforeMinor == null || ev.afterMinor == null) && ev.amountMinor != null && (
            <div className="activity-calc">
              <span>Amount</span>
              <strong>{formatMoney(ev.amountMinor)}</strong>
              <span className="activity-calc__note">(before/after not recorded for this one)</span>
            </div>
          )}
          <dl className="activity-facts">
            {ev.paidByName && (
              <div className="activity-facts__row">
                <dt>Paid by</dt>
                <dd>{ev.paidByName}</dd>
              </div>
            )}
            {ev.categoryName && (
              <div className="activity-facts__row">
                <dt>Category</dt>
                <dd>{ev.categoryName}</dd>
              </div>
            )}
            {detailFacts(ev).map(([k, v]) => (
              <div className="activity-facts__row" key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
            <div className="activity-facts__row">
              <dt>When</dt>
              <dd>{fullDateTime(ev.at)}</dd>
            </div>
            {ev.actorName && (
              <div className="activity-facts__row">
                <dt>Logged by</dt>
                <dd>{ev.actorName}</dd>
              </div>
            )}
          </dl>
          {link && (
            <Link className="activity-view" to={link.to}>
              {link.label}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </Link>
          )}
        </div>
      )}
    </li>
  );
}

// --- describe an event as a compact one-liner ---------------------------------

function describe(ev: ActivityEvent): string {
  const who = ev.actorName ?? "Someone";
  const t = ev.title ?? "";
  switch (ev.action) {
    case "spend.add": return `${who} spent at ${t}`;
    case "spend.edit": return `${who} edited a spend at ${t}`;
    case "spend.delete": return `${who} removed a spend at ${t}`;
    case "moneyin.add": return `${who} received ${t}`;
    case "moneyin.edit": return `${who} edited money in — ${t}`;
    case "moneyin.delete": return `${who} removed money in — ${t}`;
    case "bill.pay": return `${who} paid ${t}`;
    case "bill.unpay": return `${who} un-marked ${t}`;
    case "bill.payall": return `${who} marked all bills paid`;
    case "bill.resetall": return `${who} reset all bill payments`;
    case "bill.add": return `${who} added a recurring bill — ${t}`;
    case "bill.edit": return `${who} edited ${t}`;
    case "bill.archive": return `${who} archived ${t}`;
    case "bill.restore": return `${who} restored ${t}`;
    case "plan.pay": return `${who} paid ${t}${payNo(ev)}`;
    case "plan.unpay": return `${who} un-marked ${t}${payNo(ev)}`;
    case "plan.add": return `${who} added a payment plan — ${t}`;
    case "plan.edit": return `${who} edited plan ${t}`;
    case "plan.archive": return `${who} archived plan ${t}`;
    case "plan.restore": return `${who} restored plan ${t}`;
    case "balance.update": return `${who} updated ${t}'s balance`;
    case "savings.add": return `${who} created goal “${t}”`;
    case "savings.addmoney": return `${who} added money to “${t}”`;
    case "savings.reduce": return `${who} reduced “${t}”`;
    case "savings.delete": return `${who} deleted goal “${t}”`;
    case "budget.add": return `${who} added a budget — ${t}`;
    case "budget.edit": return `${who} edited budget ${t}`;
    case "budget.delete": return `${who} deleted budget ${t}`;
    case "income.update": return `${who} updated salaries`;
    case "category.add": return `${who} added category ${t}`;
    case "category.edit": return `${who} edited category ${t}`;
    case "category.delete": return `${who} deleted category ${t}`;
    case "type.add": return `${who} added type ${t}`;
    case "type.edit": return `${who} edited type ${t}`;
    case "type.delete": return `${who} deleted type ${t}`;
    case "member.add": return `${who} added ${t}`;
    case "member.edit": return `${who} edited ${t}`;
    case "member.delete": return `${who} removed ${t}`;
    case "settings.rename": return `${who} renamed the tracker to “${t}”`;
    case "paycycle.set": return `${who} set the ${t}`;
    default: return `${who} · ${ev.action}${t ? ` · ${t}` : ""}`;
  }
}

function payNo(ev: ActivityEvent): string {
  const p = ev.detail?.payment;
  const of = ev.detail?.of;
  return typeof p === "number" && typeof of === "number" ? ` (payment ${p} of ${of})` : "";
}

function balanceLabel(ev: ActivityEvent): string {
  if (ev.entity === "balance") return "Balance";
  if (ev.entity === "savings") return "Saved";
  return "In account";
}

// Direction of the money movement, from before/after when we have it, else from
// the action. Drives the +/− and the red/green colour.
function movement(ev: ActivityEvent): { minor: number | null; dir: "up" | "down" | "flat" } {
  if (!ev.isMoney) return { minor: null, dir: "flat" };
  if (ev.beforeMinor != null && ev.afterMinor != null) {
    const delta = ev.afterMinor - ev.beforeMinor;
    if (ev.entity === "balance") return { minor: delta, dir: delta === 0 ? "flat" : delta > 0 ? "up" : "down" };
    return { minor: delta, dir: delta > 0 ? "up" : delta < 0 ? "down" : "flat" };
  }
  const up = new Set(["moneyin.add", "bill.unpay", "bill.resetall", "plan.unpay", "spend.delete", "savings.addmoney"]);
  const down = new Set(["spend.add", "bill.pay", "bill.payall", "plan.pay", "savings.reduce", "moneyin.delete"]);
  const dir = up.has(ev.action) ? "up" : down.has(ev.action) ? "down" : "flat";
  return { minor: ev.amountMinor, dir };
}

function detailFacts(ev: ActivityEvent): [string, string][] {
  const d = ev.detail ?? {};
  const out: [string, string][] = [];
  if (typeof d.occurredOn === "string") out.push(["Spend date", d.occurredOn]);
  if (typeof d.note === "string" && d.note) out.push(["Note", d.note]);
  if (typeof d.period === "string") out.push(["For month", d.period]);
  if (typeof d.asOf === "string") out.push(["Balance as of", d.asOf]);
  if (typeof d.frequency === "string") out.push(["How often", d.frequency]);
  if (typeof d.cadence === "string") out.push(["How often", d.cadence]);
  if (typeof d.provider === "string" && d.provider) out.push(["Provider", d.provider]);
  if (typeof d.numPayments === "number") out.push(["Payments", String(d.numPayments)]);
  if (typeof d.type === "string") out.push(["Type", d.type]);
  if (d.reversed === true) out.push(["Note", "This reverses an earlier entry"]);
  if (d.backfill === 1) out.push(["Source", "Rebuilt from your history"]);
  return out;
}

// --- date grouping & relative time --------------------------------------------

interface DayGroup { key: string; label: string; events: ActivityEvent[]; }

function groupByDay(events: ActivityEvent[]): DayGroup[] {
  const groups: DayGroup[] = [];
  const byKey = new Map<string, DayGroup>();
  for (const ev of events) {
    const d = new Date(ev.at);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    let g = byKey.get(key);
    if (!g) {
      g = { key, label: dayLabel(d), events: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    g.events.push(ev);
  }
  return groups;
}

function dayLabel(d: Date): string {
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: days > 300 ? "numeric" : undefined });
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fullDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}
